import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Typography, TextField, Button, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, CircularProgress, Alert, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, TablePagination,
} from '@mui/material'
import { Search, AlertTriangle, XCircle, CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { abrogateDocument, resetAbrogateFlow } from '../../../redux/actions/dataUpdateAction'

export default function AbrogateDocumentTab() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { countryCode, docType, abrogateFlow } = useSelector((s) => s.dataUpdate)
  const { submitting, success, error } = abrogateFlow

  const [query, setQuery] = useState('')
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [abrogatedBy, setAbrogatedBy] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [searchResults, setSearchResults] = useState({ results: [], total: 0, loading: false, page: 1 })

  const handleSearch = async (page = 1) => {
    setSearchResults((s) => ({ ...s, loading: true }))
    try {
      const { backendClient } = await import('../../../api/backend')
      const data = await backendClient.searchDataUpdateDocuments({
        query, country_code: countryCode, doc_type: docType, page, page_size: 15, active_only: true,
      })
      setSearchResults({ results: data.results || [], total: data.total || 0, loading: false, page })
    } catch (e) {
      setSearchResults((s) => ({ ...s, loading: false }))
    }
  }

  const handleConfirmAbrogate = () => {
    if (!selectedDoc || !abrogatedBy.trim()) return
    dispatch(abrogateDocument(selectedDoc.id, { abrogated_by: abrogatedBy, doc_type: docType }))
    setConfirmOpen(false)
  }

  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CheckCircle size={64} color="#4caf50" style={{ marginBottom: 16 }} />
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>{t('du_document_abrogated')}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('du_marked_inactive')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            dispatch(resetAbrogateFlow())
            setSelectedDoc(null)
            setAbrogatedBy('')
            setSearchResults({ results: [], total: 0, loading: false, page: 1 })
          }}
          sx={{
            backgroundColor: '#c9a96e', '&:hover': { backgroundColor: '#b8955a' },
            textTransform: 'none', fontWeight: 600,
          }}
        >
          {t('du_done')}
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder={t('du_search_active_documents')}
          size="small" fullWidth value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search size={18} /></InputAdornment>,
          }}
        />
        <Button
          variant="contained"
          onClick={() => handleSearch()}
          disabled={searchResults.loading}
          sx={{
            backgroundColor: '#c9a96e', '&:hover': { backgroundColor: '#b8955a' },
            textTransform: 'none', minWidth: 100,
          }}
        >
          {t('du_search')}
        </Button>
      </Box>

      {searchResults.loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress sx={{ color: '#c9a96e' }} /></Box>
      ) : searchResults.results.length > 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('du_title_header')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('du_number')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('du_language')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('du_date')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {searchResults.results.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.title}
                  </TableCell>
                  <TableCell>{doc.doc_number || '-'}</TableCell>
                  <TableCell>
                    <Chip label={doc.language?.toUpperCase()} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{doc.issue_date ? new Date(doc.issue_date).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<XCircle size={14} />}
                      sx={{ textTransform: 'none' }}
                      onClick={() => {
                        setSelectedDoc(doc)
                        setConfirmOpen(true)
                      }}
                    >
                      {t('du_abrogate')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {searchResults.total > 15 && (
            <TablePagination
              component="div"
              count={searchResults.total}
              page={searchResults.page - 1}
              rowsPerPage={15}
              rowsPerPageOptions={[15]}
              onPageChange={(_, p) => handleSearch(p + 1)}
            />
          )}
        </Paper>
      ) : query ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {t('du_no_active_documents')}
        </Typography>
      ) : (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {t('du_search_to_abrogate')}
        </Typography>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AlertTriangle size={20} color="#f44336" />
          {t('du_confirm_abrogation')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('du_about_to_abrogate')} <strong>{selectedDoc?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('du_abrogate_warning')}
          </Typography>
          <TextField
            label={t('du_abrogated_by')}
            placeholder={t('du_abrogated_by_placeholder')}
            fullWidth size="small"
            value={abrogatedBy}
            onChange={(e) => setAbrogatedBy(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none' }}>{t('du_cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!abrogatedBy.trim() || submitting}
            onClick={handleConfirmAbrogate}
            sx={{ textTransform: 'none' }}
          >
            {submitting ? t('du_processing_message') : t('du_confirm_abrogate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
